import { batch, createSignal, type JSX } from 'solid-js';
import { t } from '../i18n';
import { type RepoIssueRow, useRepoIssues } from '../lib/issues';
import { usePagedQuery } from '../lib/paged';
import { IssueDetailDrawer } from './IssueDetailDrawer';
import { LoadMore } from './LoadMore';
import { Panel } from './Panel';
import { QueryBoundary } from './QueryBoundary';
import { RepoIssuesFilters } from './RepoIssuesFilters';
import { RepoIssuesTable } from './RepoIssuesTable';
import type { RepoTabProps } from './RepoOverviewTab';
import { Skeleton } from './Skeleton';

// Repo detail → Issues (arc 101 slice 06): the tracked issues the agent has
// touched, filterable by state + qualification off `…/issues`. The list is the
// window onto what the loop decided; the drawer behind "Details" is where a
// human forces an escalation or a handoff the policy wouldn't have.
// Paging is `lib/paged` — same contract as the Audit tab, which pages the same
// grammar.

export function RepoIssuesTab(props: RepoTabProps): JSX.Element {
  const [state, setState] = createSignal('');
  const [qualification, setQualification] = createSignal('');
  const [selected, setSelected] = createSignal<RepoIssueRow | null>(null);

  const filters = () => ({ state: state(), qualification: qualification() });
  const list = usePagedQuery<RepoIssueRow>((cursor) =>
    useRepoIssues(() => props.encoded, filters, cursor, props.token),
  );

  // Reset in the SAME batch as the filter set, so the query key jumps straight
  // to `cursor=null` — one fetch of the new filter's page one, never a throwaway
  // at the old cursor. `reset()` also drops the prior filter's rows.
  const applyFilter = (set: (v: string) => void, v: string): void =>
    batch(() => {
      set(v);
      list.reset();
    });

  return (
    <>
      <Panel title={t('repo_detail.issues_title')} footer={t('repo_detail.issues_footer')}>
        <RepoIssuesFilters
          state={state()}
          qualification={qualification()}
          onState={(v) => applyFilter(setState, v)}
          onQualification={(v) => applyFilter(setQualification, v)}
        />

        <QueryBoundary
          query={list.view}
          isEmpty={(r) => r.length === 0}
          loading={<Skeleton height="14rem" />}
          empty={<div class="list-empty">{t('repo_detail.issues_empty')}</div>}
        >
          {(r) => (
            <>
              <RepoIssuesTable
                rows={r}
                encoded={props.encoded}
                onOpen={(row) => setSelected(row)}
              />
              <LoadMore list={list} label={t('repo_detail.issues_load_more')} />
            </>
          )}
        </QueryBoundary>
      </Panel>

      {/* Sibling of the panel, not a child — the overlay owns the viewport.
          The drawer's re-read patches ONE row (`list.patch`); refetching the
          list would throw the accumulated pages away and lose the operator's
          place mid-triage. */}
      <IssueDetailDrawer
        issue={selected()}
        encoded={props.encoded}
        token={props.token}
        onClose={() => setSelected(null)}
        onUpdated={(fresh) => list.patch(fresh)}
      />
    </>
  );
}
